import { useState, useCallback, useEffect, useRef } from 'react';
import api from '../../utils/api';

const CART_KEY = 'bakra_cart';

const loadCart = () => {
  try {
    const raw = localStorage.getItem(CART_KEY);
    const items = raw ? JSON.parse(raw) : [];
    return items.map(item => ({
      ...item,
      image: item.image?.startsWith('http://localhost') ? null : item.image
    }));
  } catch { return []; }
};

const saveCart = (items) => {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
};

export function useCart(user) {
  const [cart, setCartRaw] = useState(loadCart);
  const isSynced = useRef(false);
  const syncTimer = useRef(null);

  // On login — fetch cart from DB
  useEffect(() => {
    if (!user?.id) return;
    isSynced.current = false;

    const cartApi = user.role === 'butcher'
      ? '/api/butcher/cart'
      : '/api/customer/cart';

    api.get(cartApi)
      .then(({ data }) => {
        const dbCart = data.map(i => ({
          id:       i.animal_id,
          name:     i.name,
          price:    Number(i.price),
          image:    i.image,
          category: i.category,
          quantity: i.quantity,
        }));
        saveCart(dbCart);
        setCartRaw(dbCart);
      })
      .catch(() => {})
      .finally(() => {
        isSynced.current = true;
      });
  }, [user?.id]);

  const setCart = useCallback((updater) => {
    setCartRaw(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      saveCart(next);

      if (user?.id && isSynced.current) {
        const cartApi = user.role === 'butcher'
          ? '/api/butcher/cart/sync'
          : '/api/customer/cart/sync';

        // Debounce — only sync after 500ms of no changes
        clearTimeout(syncTimer.current);
        syncTimer.current = setTimeout(() => {
          api.post(cartApi, { items: next }).catch(() => {});
        }, 500);
      }
      return next;
    });
  }, [user?.id, user?.role]);

  const addToCart = useCallback((item, qty = 1) => {
    setCart(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) {
        return prev.map(i => i.id === item.id
          ? { ...i, quantity: Math.min((i.quantity || 1) + qty, item.stock || 99) }
          : i
        );
      }
      return [...prev, {
        id:        item.id,
        name:      item.name,
        price:     Number(item.price_inr || item.price || 0),
        image:     item.image_url || item.image,
        category:  item.category,
        seller_id: item.seller_id,
        quantity:  qty,    
        stock:     item.quantity || item.stock_qty || 1,
      }];
    });
  }, [setCart]);

  const removeFromCart = useCallback((id) => {
    setCart(prev => prev.filter(i => i.id !== id));
  }, [setCart]);

  const clearCart = useCallback(() => {
    setCart([]);
  }, [setCart]);

  const isInCart = useCallback((id) => {
    return cart.some(i => i.id === id);
  }, [cart]);

  const totalPrice = cart.reduce(
    (sum, i) => sum + (Number(i.price || 0) * (i.quantity || 1)), 0
  );

  const flushSync = useCallback(() => {
    if (!user?.id || !isSynced.current) return;
    clearTimeout(syncTimer.current);
    const cartApi = user.role === 'butcher'
      ? '/api/butcher/cart/sync'
      : '/api/customer/cart/sync';
    const current = loadCart();
    return api.post(cartApi, { items: current }).catch(() => {});
  }, [user?.id, user?.role]);

  return { cart, addToCart, removeFromCart, clearCart, isInCart, totalPrice, flushSync };
}